// A match being played on the server.
//
// Holds no engine. The state it exposes is whatever the server last sent --
// already redacted for this client -- and the legal actions are the server's
// list rather than one computed here, so a modified client cannot invent a
// move. Submitting posts the action and waits: the board redraws when the
// server says the game changed, not when this client hopes it did.
//
// A spectator is the same object with no seat: `humanActions` is empty, so
// every gesture the board offers is simply never legal.

import type { LoadedRules } from '../rules/load.ts';
import type { Action, GameEvent, GameState, ObjectId, PlayerId } from '../rules/types.ts';
import type { Arrangement } from './layout.ts';
import { MatchLog, REPLAY_VERSION, type Replay } from './log.ts';
import type { Match } from './match.ts';

/** One frame of a match, as the server sends it. */
export interface MatchFrame {
  table: string;
  /** The seat this client holds, or null for a spectator. */
  you: PlayerId | null;
  seats: string[];
  state: GameState;
  actions: Action[];
  over: boolean;
  winner: PlayerId | null;
  /** Where both players have arranged their bases. Not game state (see Match). */
  arrangement?: Arrangement;
}

export class RemoteMatch implements Match {
  readonly rules: LoadedRules;
  readonly log: MatchLog;
  /** The table this match belongs to, for addressing actions at it. */
  readonly table: string;

  state: GameState;
  aiDelayMs = 0;
  onChange: (() => void) | null = null;
  onEvents: ((events: GameEvent[]) => void) | null = null;

  private frame: MatchFrame;
  private readonly send: (table: string, action: Action) => Promise<boolean>;
  private readonly sendArrange:
    ((table: string, object: ObjectId, cell: [number, number]) => Promise<void>) | null;
  private eventCursor = 0;
  /**
   * The arrangement as last seen, plus anything this client has just done.
   *
   * The server's copy is authoritative and overwrites this on the next frame.
   * Holding the local move in the meantime is what stops a dragged card
   * snapping back to its old cell for the length of a round trip.
   */
  private arranged: Arrangement = {};

  constructor(
    rules: LoadedRules, frame: MatchFrame,
    send: (table: string, action: Action) => Promise<boolean>,
    sendArrange:
      ((table: string, object: ObjectId, cell: [number, number]) => Promise<void>) | null = null,
  ) {
    this.rules = rules;
    this.frame = frame;
    this.table = frame.table;
    this.state = frame.state;
    this.send = send;
    this.sendArrange = sendArrange;
    this.arranged = frame.arrangement ?? {};
    // Watching from seat 0 is the spectator's view; nothing about the board
    // depends on holding the seat, only on which way round it is drawn.
    this.log = new MatchLog(rules, frame.you ?? 0);
  }

  get human(): PlayerId {
    return this.frame.you ?? 0;
  }

  /** True only for a seated player the server is actually waiting on. */
  get waitingOnHuman(): boolean {
    return this.frame.you !== null && this.frame.actions.length > 0;
  }

  get isOver(): boolean {
    return this.frame.over;
  }

  /** Who holds each seat, by Discord id, so the HUD can name the opponent. */
  get seats(): string[] {
    return this.frame.seats;
  }

  get seated(): boolean {
    return this.frame.you !== null;
  }

  humanActions(): Action[] {
    return this.frame.actions;
  }

  get arrangement(): Arrangement {
    return this.arranged;
  }

  /**
   * Puts a card on a cell and tells the table.
   *
   * A spectator may not: they have no cards to arrange, and the server would
   * refuse it anyway -- the seat is taken from who is calling, never from the
   * message, which is the same rule every action goes through.
   */
  arrange(object: ObjectId, cell: [number, number]): void {
    if (!this.seated) return;
    this.arranged[object] = cell;
    void this.sendArrange?.(this.table, object, cell);
    this.onChange?.();
  }

  /**
   * Sends an action and reports that it was *sent*, not that it succeeded.
   *
   * The board treats `false` as "that was refused, say why", and a server round
   * trip cannot answer that synchronously. What protects the game is that the
   * server re-checks the action anyway: a client that mispredicts simply sees
   * the next frame arrive unchanged.
   */
  submit(action: Action): boolean {
    if (!this.seated) return false;
    if (!this.frame.actions.some((a) => JSON.stringify(a) === JSON.stringify(action))) {
      return false;
    }
    void this.send(this.table, action);
    return true;
  }

  /** Takes a new frame from the server and tells the view about it. */
  update(frame: MatchFrame): void {
    this.frame = frame;
    this.state = frame.state;
    if (frame.arrangement) this.arranged = frame.arrangement;

    // The event stream only ever grows within a match, so the cursor is a
    // count rather than a diff. A frame that arrives with fewer events than
    // last time is a new game, and starts the count again.
    if (frame.state.events.length < this.eventCursor) this.eventCursor = 0;
    const fresh = frame.state.events.slice(this.eventCursor);
    this.eventCursor = frame.state.events.length;

    if (fresh.length > 0) this.onEvents?.(fresh);
    this.onChange?.();
  }

  /**
   * There is no taking a move back from someone who already saw it.
   *
   * Not an oversight and not a limitation of the transport: an undo in a game
   * against a person is a thing both people have to agree to, and silently
   * rolling one side back would desynchronise the match.
   */
  rewind(): boolean {
    return false;
  }

  readonly canRewind = false;

  /**
   * A replay of what this client saw.
   *
   * Deliberately not the seed-and-actions form a local game produces: this
   * client never held the seed, which is the point, so it cannot rebuild the
   * match. What it can offer is the log it drew from.
   */
  replay(): Replay {
    return {
      version: REPLAY_VERSION,
      seed: `table:${this.table}`,
      difficulty: 'pvp',
      human: this.human,
      actions: [],
      recorded: new Date().toISOString(),
    };
  }
}
